// Shared shape for the recovery-routes intent bridge rendered on code pages
// (RCRA, state, Basel, EWC). Routes are regulation-sourced and dollar-free —
// free surfaces show recovery tiers only, never pricing.

export type RecoveryTier = 'High' | 'Medium' | 'Low' | 'Specialist';

export interface RecoveryRoute {
  title: string;
  tier: RecoveryTier;
  detail: string;
  source: string;
  sourceUrl: string;
}

export const route = (
  title: string,
  tier: RecoveryTier,
  detail: string,
  src: { source: string; sourceUrl: string },
): RecoveryRoute => ({ title, tier, detail, ...src });

const ECFR = 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I';

// Federal recovery-pathway citations shared across code systems.
export const FEDERAL_SOURCES = {
  reclamation: {
    source: '40 CFR 261.1(c)(4) — reclamation',
    sourceUrl: `${ECFR}/part-261/subpart-A/section-261.1`,
  },
  ccpReclaim: {
    source: '40 CFR 261.2(c)(3) Table 1 — commercial chemical products being reclaimed',
    sourceUrl: `${ECFR}/part-261/subpart-A/section-261.2`,
  },
  closedLoop: {
    source: '40 CFR 261.4(a)(8) — closed-loop reclamation exclusion',
    sourceUrl: `${ECFR}/part-261/subpart-A/section-261.4`,
  },
  oilReinsertion: {
    source: '40 CFR 261.4(a)(12) — oil-bearing residues returned to refining',
    sourceUrl: `${ECFR}/part-261/subpart-A/section-261.4`,
  },
  scrapMetal: {
    source: '40 CFR 261.4(a)(13) — scrap metal being recycled',
    sourceUrl: `${ECFR}/part-261/subpart-A/section-261.4`,
  },
  preciousMetals: {
    source: '40 CFR Part 266 Subpart F — precious metal recovery',
    sourceUrl: `${ECFR}/part-266/subpart-F`,
  },
  leadAcid: {
    source: '40 CFR Part 266 Subpart G — spent lead-acid batteries being reclaimed',
    sourceUrl: `${ECFR}/part-266/subpart-G`,
  },
  energyRecovery: {
    source: '40 CFR Part 266 Subpart H — burning for energy recovery',
    sourceUrl: `${ECFR}/part-266/subpart-H`,
  },
  usedOil: {
    source: '40 CFR Part 279 — used oil management standards',
    sourceUrl: `${ECFR}/part-279`,
  },
  ldr: {
    source: '40 CFR 268.40 — LDR treatment standards',
    sourceUrl: `${ECFR}/part-268/subpart-D/section-268.40`,
  },
  htmr: {
    source: '40 CFR 268.42 Table 1 — HTMR technology standard',
    sourceUrl: `${ECFR}/part-268/subpart-D/section-268.42`,
  },
  definitions: {
    source: '40 CFR 260.10 — definitions',
    sourceUrl: `${ECFR}/part-260/subpart-B/section-260.10`,
  },
  tscaPcb: {
    source: '40 CFR Part 761 — TSCA PCB decontamination and disposal',
    sourceUrl: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-R/part-761',
  },
} as const;
